import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { downloadXLSX, downloadZIP } from "@shared/utils/utils";
import { useNavigate } from "react-router-dom";
import {
  approveBills,
  downloadBills,
  draftBillingCycle,
  estimateMeterReadings,
  excludeConsumersFromBilling,
  fetchReadingsForExcludedConsumers,
  generateBillsForExcludedConsumers,
  getActivityLog,
  getBillCycleSummary,
  getBillDaysThresholdList,
  getBillDaysThresholdSummary,
  getBillingCycleDetails,
  getBillingCyclesList,
  getBillingPlans,
  getBillingSummary,
  getBillRunBillsList,
  getBillRuns,
  getBillRunSummary,
  getBillsList,
  getBillTemplatesList,
  getReceiptBillTemplates,
  getConsumerAnalysis,
  getConsumersList,
  getDashboardSummary,
  getDistributionOverview,
  getDraftDetails,
  getExcludedConsumers,
  getExcludedConsumersList,
  getExcludedConsumersSummary,
  getExportBillData,
  getFailedBillsList,
  getMeterReadingAnalysis,
  getMeterReadings,
  getMeterReadingSummary,
  getMeterReplacementCases,
  getOutstandingAccounts,
  getPaymentDetails,
  getPlanDetails,
  getRunDetails,
  getServiceCharges,
  getTrueUpCases,
  getTrueUpSummary,
  rejectBills,
  saveBillingConfiguration,
  startBillGeneration,
  trackBillGenerationProgress,
  updateBillingConfiguration,
  updateMeterConfiguration,
} from "./api";

// ============================================================================
// STEP 1: Bill Cycle Hooks
// ============================================================================

export const useReceiptBillTemplates = () => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    ["bx", "bulkBilling", "receiptBillTemplates", REMOTE_UTILITY_ID],
    () => getReceiptBillTemplates(),
    {
      refetchOnWindowFocus: false,
    }
  );
};

export const useBillingCyclesList = () => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billingCyclesList(REMOTE_UTILITY_ID),
    () => getBillingCyclesList(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  );
};

export const useBillingCycleDetails = (cycleId: string | number) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billingCycleDetails(cycleId),
    () => getBillingCycleDetails(cycleId),
    {
      enabled: !!cycleId,
    },
  );
};

export const useBillTemplatesList = () => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billTemplatesList(REMOTE_UTILITY_ID),
    () => getBillTemplatesList(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  );
};

export const useSaveBillingConfiguration = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return saveBillingConfiguration(payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Billing configuration saved successfully",
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "bx" &&
            query.queryKey[1] === "bulkBilling" &&
            query.queryKey[2] === "draftDetails",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to save billing configuration",
          variant: "destructive",
        });
      },
    },
  );
};

export const useDraftBillingCycle = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return draftBillingCycle(payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Billing cycle draft created successfully",
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "bx" &&
            query.queryKey[1] === "bulkBilling" &&
            query.queryKey[2] === "draftDetails",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create billing cycle draft",
          variant: "destructive",
        });
      },
    },
  );
};

export const useDraftDetails = (draftId: string | number) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.draftDetails(draftId),
    () => getDraftDetails(draftId),
    {
      enabled: !!draftId,
    },
  );
};

// ============================================================================
// STEP 2: Fetch Consumer Data Hooks
// ============================================================================

export const useDashboardSummary = (cycleId: string | number) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.dashboardSummary(
      REMOTE_UTILITY_ID,
      cycleId,
    ),
    () => getDashboardSummary(cycleId),
    {
      enabled: !!cycleId,
    },
  );
};

export const useConsumerAnalysis = (cycleId: string | number) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.consumerAnalysis(
      REMOTE_UTILITY_ID,
      cycleId,
    ),
    () => getConsumerAnalysis(cycleId),
    {
      enabled: !!cycleId,
    },
  );
};

export const useConsumersList = (
  cycleId: string | number,
  page: number = 1,
  limit: number = 50,
) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.consumersList(
      REMOTE_UTILITY_ID,
      cycleId,
      page,
      limit,
    ),
    () => getConsumersList(cycleId, page, limit),
    {
      enabled: !!cycleId,
    },
  );
};

export const useBillingPlans = (cycleId: string | number) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billingPlans(
      REMOTE_UTILITY_ID,
      cycleId,
    ),
    () => getBillingPlans(cycleId),
    {
      enabled: !!cycleId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  );
};

export const usePlanDetails = (planId: string | number) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.planDetails(
      REMOTE_UTILITY_ID,
      planId,
    ),
    () => getPlanDetails(planId),
    {
      enabled: !!planId,
    },
  );
};

export const useOutstandingAccounts = (
  cycleId: string | number,
  page: number = 1,
  limit: number = 50,
  sortBy: string = "outstanding",
  sortOrder: string = "desc",
) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.outstandingAccounts(
      REMOTE_UTILITY_ID,
      cycleId,
      page,
      limit,
      sortBy,
      sortOrder,
    ),
    () => getOutstandingAccounts(cycleId, page, limit, sortBy, sortOrder),
    {
      enabled: !!cycleId,
    },
  );
};

export const usePaymentDetails = (cycleId: string | number) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.paymentDetails(
      REMOTE_UTILITY_ID,
      cycleId,
    ),
    () => getPaymentDetails(cycleId),
    {
      enabled: !!cycleId,
    },
  );
};

export const useUpdateBillingConfiguration = (billDraftId: string | number) => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return updateBillingConfiguration(billDraftId, payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Billing configuration updated successfully",
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "bx" &&
            query.queryKey[1] === "bulkBilling" &&
            (query.queryKey[2] === "draftDetails" ||
              query.queryKey[2] === "dashboardSummary"),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to update billing configuration",
          variant: "destructive",
        });
      },
    },
  );
};

// ============================================================================
// STEP 3: Meter Reading Hooks
// ============================================================================

export const useMeterReadingSummary = (cycleId: string | number) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.meterReadingSummary(
      REMOTE_UTILITY_ID,
      cycleId,
    ),
    () => getMeterReadingSummary(cycleId),
    {
      enabled: !!cycleId,
    },
  );
};

export const useMeterReadingAnalysis = (cycleId: string | number) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.meterReadingAnalysis(cycleId),
    () => getMeterReadingAnalysis(cycleId),
    {
      enabled: !!cycleId,
    },
  );
};

export const useMeterReadings = (
  cycleId: string | number,
  draftId: string | number,
  filters?: {
    readingType?: string;
    search?: string;
    meterType?: string;
    service?: string;
    veeStatus?: string;
    status?: string;
    consumptionFlag?: string;
    page?: number;
    limit?: number;
  },
) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.meterReadings(
      REMOTE_UTILITY_ID,
      cycleId,
      draftId,
      filters,
    ),
    () => getMeterReadings(cycleId, draftId, filters),
    {
      enabled: !!cycleId,
    },
  );
};

export const useMeterReplacementCases = (
  cycleId: string | number,
  page: number,
  limit: number,
  search?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.meterReplacementCases(
      cycleId,
      page,
      limit,
      search,
    ),
    () => getMeterReplacementCases(cycleId, page, limit, search),
    {
      enabled: !!cycleId,
    },
  );
};

export const useTrueUpSummary = (cycleId: string | number) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.trueUpSummary(cycleId),
    () => getTrueUpSummary(cycleId),
    {
      enabled: !!cycleId,
    },
  );
};

export const useTrueUpCases = (
  cycleId: string | number,
  page: number,
  limit: number,
  search?: string,
  status?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.trueUpCases(
      cycleId,
      page,
      limit,
      search,
      status,
    ),
    () => getTrueUpCases(cycleId, page, limit, search, status),
    {
      enabled: !!cycleId,
    },
  );
};

export const useBillDaysThresholdSummary = (cycleId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billDaysThresholdSummary(cycleId),
    () => getBillDaysThresholdSummary(cycleId),
  );
};

export const useBillDaysThresholdList = (
  cycleId: string | number,
  page: number = 1,
  limit: number = 50,
  threshold?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billDaysThresholdList(
      cycleId,
      page,
      limit,
      threshold,
    ),
    () => getBillDaysThresholdList(cycleId, page, limit, threshold),
    {
      enabled: !!cycleId,
    },
  );
};

export const useEstimateMeterReadings = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return estimateMeterReadings(payload);
    },
    {
      onSuccess: (data) => {
        toast({
          title: "Success!",
          description: data.message,
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "bx" &&
            query.queryKey[1] === "bulkBilling" &&
            (query.queryKey[2] === "meterReadings" ||
              query.queryKey[2] === "meterReadingSummary"),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to estimate meter readings",
          variant: "destructive",
        });
      },
    },
  );
};

export const useExcludeConsumersFromBilling = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return excludeConsumersFromBilling(payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Consumers excluded from billing successfully",
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "bx" &&
            query.queryKey[1] === "bulkBilling" &&
            (query.queryKey[2] === "consumersList" ||
              query.queryKey[2] === "draftDetails"),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to exclude consumers from billing",
          variant: "destructive",
        });
      },
    },
  );
};

export const useUpdateMeterConfiguration = (billDraftId: string | number) => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return updateMeterConfiguration(billDraftId, payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Meter configuration updated successfully",
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "bx" &&
            query.queryKey[1] === "bulkBilling" &&
            (query.queryKey[2] === "draftDetails" ||
              query.queryKey[2] === "meterReadingSummary"),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update meter configuration",
          variant: "destructive",
        });
      },
    },
  );
};

// ============================================================================
// STEP 4: Bill Generation Hooks
// ============================================================================

export const useStartBillGeneration = (billDraftId: string | number) => {
  const { toast } = useToast();

  return useSmartMutation(
    () => {
      return startBillGeneration(billDraftId);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Bill generation started successfully",
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "bx" &&
            query.queryKey[1] === "bulkBilling" &&
            query.queryKey[2] === "billCycleSummary",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to start bill generation",
          variant: "destructive",
        });
      },
    },
  );
};

export const useTrackBillGenerationProgress = (taskId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.trackBillGenerationProgress(taskId),
    () => trackBillGenerationProgress(taskId),
    {
      enabled: !!taskId,
      refetchInterval: 3000, // Poll every 3 seconds
      refetchIntervalInBackground: false,
    },
  );
};

export const useBillCycleSummary = (billCycleId: string | number) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billCycleSummary(billCycleId),
    () => getBillCycleSummary(billCycleId),
    {
      enabled: !!billCycleId,
    },
  );
};

export const useFailedBillsList = (
  runId: string | number,
  page: number = 1,
  limit: number = 50,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.failedBillsList(runId, page, limit),
    () => getFailedBillsList(runId, page, limit),
    {
      enabled: !!runId,
    },
  );
};

// ============================================================================
// STEP 5: Review Bills Hooks
// ============================================================================

export const useBillingSummary = (runId: string | number) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billingSummary(runId),
    () => getBillingSummary(runId),
    {
      enabled: !!runId,
    },
  );
};

export const useBillsList = (
  runId: string | number,
  page: number = 1,
  limit: number = 50,
  search?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billsList(runId, page, limit, search),
    () => getBillsList(runId, page, limit, search),
    {
      enabled: !!runId,
    },
  );
};

export const useExcludedConsumers = (
  runId: string | number,
  page: number = 1,
  limit: number = 50,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.excludedConsumers(runId, page, limit),
    () => getExcludedConsumers(runId, page, limit),
    {
      enabled: !!runId,
    },
  );
};

export const useDistributionOverview = (runId: string | number) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.distributionOverview(runId),
    () => getDistributionOverview(runId),
    {
      enabled: !!runId,
    },
  );
};

export const useApproveBills = (runId: string | number) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: any) => {
      return approveBills(runId, payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Bills approved successfully",
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "bx" &&
            query.queryKey[1] === "bulkBilling" &&
            (query.queryKey[2] === "billingSummary" ||
              query.queryKey[2] === "billRunSummary"),
        });
        navigate("/bx/create-bill-generation");
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to approve bills",
          variant: "destructive",
        });
      },
    },
  );
};

export const useRejectBills = (runId: string | number) => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => {
      return rejectBills(runId, payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Bills rejected successfully",
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "bx" &&
            query.queryKey[1] === "bulkBilling" &&
            (query.queryKey[2] === "billingSummary" ||
              query.queryKey[2] === "billRunSummary"),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to reject bills",
          variant: "destructive",
        });
      },
    },
  );
};

export const useDownloadBills = () => {
  return useSmartMutation(
    async ({
      runId,
      filename = "bills.zip",
    }: {
      runId: string | number;
      filename?: string;
    }) => {
      const blob = await downloadBills(runId);
      downloadZIP(blob, filename);
      return blob;
    },
  );
};

export const useBillRunSummary = (runId: string | number) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billRunSummary(runId),
    () => getBillRunSummary(runId),
    {
      enabled: !!runId,
    },
  );
};

export const useBillRunBillsList = (
  runId: string | number,
  page: number = 1,
  limit: number = 50,
  search?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billRunBillsList(
      runId,
      page,
      limit,
      search,
    ),
    () => getBillRunBillsList(runId, page, limit, search),
    {
      enabled: !!runId,
    },
  );
};

export const useExcludedConsumersSummary = (runId: string | number) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.excludedConsumersSummary(runId),
    () => getExcludedConsumersSummary(runId),
    {
      enabled: !!runId,
    },
  );
};

export const useExcludedConsumersList = (
  runId: string | number,
  page: number = 1,
  limit: number = 50,
  search?: string,
) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.excludedConsumersList(
      runId,
      page,
      limit,
      search,
    ),
    () => getExcludedConsumersList(runId, page, limit, search),
    {
      enabled: !!runId,
    },
  );
};

export const useFetchReadingsForExcludedConsumers = (batchId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.getExportMeterReading(batchId),
    () => fetchReadingsForExcludedConsumers(batchId),
    {
      enabled: !!batchId,
    },
  );
};

export const useGenerateBillsForExcludedConsumers = (
  runId: string | number,
) => {
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => {
      return generateBillsForExcludedConsumers(runId, payload);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Bills generated successfully for excluded consumers",
        });

        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "bx" &&
            query.queryKey[1] === "bulkBilling" &&
            (query.queryKey[2] === "excludedConsumersList" ||
              query.queryKey[2] === "billingSummary"),
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description:
            error?.message || "Failed to generate bills for excluded consumers",
          variant: "destructive",
        });
      },
    },
  );
};

export const useActivityLog = (runId: string | number) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.activityLog(runId),
    () => getActivityLog(runId),
    {
      enabled: !!runId,
    },
  );
};

export const useBillRuns = (filters: Record<string, any>) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.billRuns(filters),
    () => getBillRuns(filters),
  );
};

export const useRunDetail = (runId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.runDetails(runId),
    () => getRunDetails(runId),
    {
      enabled: !!runId,
    },
  );
};

export const useExportBillData = () => {
  return useSmartMutation(async (runId: string) => {
    const blob = await getExportBillData(runId);
    console.log("Exported Bill Data Blob:", blob);
    downloadXLSX(blob);
  });
};

export const useServiceCharges = (billingCycleId: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.bulkBilling.serviceCharges(billingCycleId),
    () => getServiceCharges(billingCycleId),
    {
      enabled: !!billingCycleId,
    },
  );
};
